import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getDuplicateEmails, mergeClients } from "../api/clients";
import { getCurrentSalonName } from "../utils/currentSalon";
import ModalCloseX from "../components/common/ModalCloseX";
import MergeClientsModal from "../components/clients/MergeClientsModal";
import {
  formatDateInTimeZone,
  formatDateTimeInTimeZone,
  parseBackendDateTime,
} from "../utils/datetime";
import { formatApiError } from "../utils/formatApiError";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import "./OnlineBookingSectionCompact.css";

const HELP_SECTIONS = [
  {
    heading: "What this report is",
    bullets: [
      "Shows active client profiles that share the same email in this salon.",
      "Portal activation is blocked when the same email appears on 2+ active clients.",
    ],
  },
  {
    heading: "How to fix duplicates",
    bullets: [
      "Step 1: Choose which profile to keep (recommended: most recent activity).",
      "Step 2: Use Merge to combine the duplicate profile into the kept profile.",
      "Step 3: Confirm the kept profile has the right contact details.",
      "Step 4: Retry portal activation or password reset.",
    ],
  },
  {
    heading: "Why it matters",
    bullets: [
      "Duplicate emails prevent online account activation.",
      "Password reset reliability improves after cleanup.",
    ],
  },
];

const formatDateTime = (value, timeZone) => {
  if (!value) return "";
  return formatDateTimeInTimeZone(value, timeZone);
};

const formatDate = (value, timeZone) => {
  if (!value) return "";
  return formatDateInTimeZone(value, timeZone);
};

const getLastActivity = (client) => {
  const ticket = parseBackendDateTime(client.last_ticket_at);
  const appt = parseBackendDateTime(client.last_appointment_at);
  if (ticket && appt) return ticket > appt ? ticket : appt;
  return ticket || appt;
};

const toMergeClient = (client) => {
  if (!client) return null;
  return {
    ...client,
    id: client.id ?? client.client_id,
    client_id: client.client_id ?? client.id,
  };
};

export default function ClientsDuplicateEmails() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const salonName = getCurrentSalonName(user);
  const salonTimeZone =
    user?.salon_timezone ??
    (typeof window !== "undefined"
      ? window.localStorage.getItem("salon_timezone")
      : null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({});
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeClientsList, setMergeClientsList] = useState([]);
  const [mergeTargetClient, setMergeTargetClient] = useState(null);
  const [mergeSourceClient, setMergeSourceClient] = useState(null);
  const [toastMessage, setToastMessage] = useState("");
  const toastTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const showToast = (message) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToastMessage(message);
    toastTimerRef.current = setTimeout(() => {
      setToastMessage("");
    }, 2400);
  };

  const loadDuplicates = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getDuplicateEmails(token);
      const nextItems = Array.isArray(data?.items) ? data.items : [];
      setItems(nextItems);
      if (nextItems.length === 1) {
        const email = nextItems[0]?.email_normalized;
        if (email) setExpanded({ [email]: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadDuplicates();
  }, [loadDuplicates]);

  const toggleExpanded = (email) => {
    setExpanded((prev) => ({
      ...prev,
      [email]: !prev[email],
    }));
  };

  const handleOpenMerge = (group, client) => {
    const clients = (group?.clients || []).map(toMergeClient).filter(Boolean);
    const clickedClient = toMergeClient(client);
    const suggestedClient =
      clients.find((item) => item.id === group?.suggested_primary_client_id) ||
      clients[0] ||
      null;
    if (!clickedClient || !suggestedClient) return;

    const targetClient =
      clickedClient.id === suggestedClient.id ? clickedClient : suggestedClient;
    const sourceClient =
      clickedClient.id === targetClient.id
        ? clients.find((item) => item.id !== targetClient.id) || null
        : clickedClient;

    setMergeClientsList(clients);
    setMergeTargetClient(targetClient);
    setMergeSourceClient(sourceClient);
    setMergeOpen(true);
  };

  const headerMeta = useMemo(() => {
    if (!salonName) return null;
    return `Salon: ${salonName}`;
  }, [salonName]);

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="onlineBookingShell min-h-screen bg-slate-100">
      <div className="onlineBookingWide online-booking-spacious online-booking-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Duplicate Emails
            </h1>
            <p className="text-sm md:text-base text-rose-100">
              Find and clean up client profiles that share the same email. This
              prevents online account activation until fixed.
            </p>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className="px-6 py-6 space-y-6">
            {headerMeta && (
              <div className="text-sm text-slate-600">{headerMeta}</div>
            )}

            <ReportHelpCard
              title="How to resolve duplicate emails"
              subtitle="Use this quick checklist before re-inviting clients."
              sections={HELP_SECTIONS}
            />

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-sm text-slate-600">Loading duplicates…</div>
            ) : error ? null : items.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
                No duplicate emails found.
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((group) => {
                  const email = group.email_normalized;
                  const expandedOpen = Boolean(expanded[email]);
                  const suggestedClient = group.clients?.find(
                    (client) =>
                      client.client_id === group.suggested_primary_client_id
                  );
                  return (
                    <div
                      key={email}
                      className="rounded-2xl border border-slate-200 bg-white shadow-sm"
                    >
                      <button
                        type="button"
                        onClick={() => toggleExpanded(email)}
                        className="w-full px-5 py-4 flex flex-wrap items-center justify-between gap-4 text-left"
                      >
                        <div className="space-y-1">
                          <div className="text-base md:text-lg font-semibold text-slate-900">
                            {email || "(missing email)"}
                          </div>
                          <div className="text-sm text-slate-600">
                            Suggested primary:{" "}
                            {suggestedClient
                              ? `${suggestedClient.full_name} (ID ${suggestedClient.client_id})`
                              : "—"}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-700">
                            {group.count} profile{group.count === 1 ? "" : "s"}
                          </span>
                          <span
                            className={`text-slate-500 transition-transform ${
                              expandedOpen ? "rotate-180" : ""
                            }`}
                          >
                            ▼
                          </span>
                        </div>
                      </button>

                      {expandedOpen && (
                        <div className="border-t border-slate-200 px-5 py-4">
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="text-left text-slate-500">
                                  <th className="py-2 pr-4">Client ID</th>
                                  <th className="py-2 pr-4">Full Name</th>
                                  <th className="py-2 pr-4">Email</th>
                                  <th className="py-2 pr-4">Phone</th>
                                  <th className="py-2 pr-4">Active</th>
                                  <th className="py-2 pr-4">Created</th>
                                  <th className="py-2 pr-4">Last Activity</th>
                                  <th className="py-2 pr-4">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.clients.map((client) => {
                                  const isPrimary =
                                    client.client_id ===
                                    group.suggested_primary_client_id;
                                  const lastActivity = getLastActivity(client);
                                  return (
                                    <tr
                                      key={client.client_id}
                                      className="border-t border-slate-100"
                                    >
                                      <td className="py-3 pr-4 font-semibold text-slate-900">
                                        {client.client_id}
                                      </td>
                                      <td className="py-3 pr-4">
                                        <div className="flex items-center gap-2">
                                          <span className="text-slate-900">
                                            {client.full_name}
                                          </span>
                                          {isPrimary && (
                                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                              Suggested Primary
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="py-3 pr-4 text-slate-600">
                                        {client.email || "—"}
                                      </td>
                                      <td className="py-3 pr-4 text-slate-600">
                                        {client.phone || "—"}
                                      </td>
                                      <td className="py-3 pr-4 text-slate-600">
                                        {client.is_active ? "Yes" : "No"}
                                      </td>
                                      <td className="py-3 pr-4 text-slate-600">
                                        {formatDate(client.created_at, salonTimeZone) || "—"}
                                      </td>
                                      <td className="py-3 pr-4 text-slate-600">
                                        {lastActivity
                                          ? formatDateTime(lastActivity, salonTimeZone)
                                          : "—"}
                                      </td>
                                      <td className="py-3 pr-4">
                                        <button
                                          type="button"
                                          onClick={() => handleOpenMerge(group, client)}
                                          className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                        >
                                          Merge
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => navigate("/manager/schedule")}
                className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Back to Schedule
              </button>
            </div>
          </div>
        </div>
      </div>

      <MergeClientsModal
        isOpen={mergeOpen}
        onClose={() => {
          setMergeOpen(false);
          setMergeTargetClient(null);
          setMergeSourceClient(null);
          setMergeClientsList([]);
        }}
        clients={mergeClientsList}
        targetClient={mergeTargetClient}
        initialSourceClient={mergeSourceClient}
        mergeClients={mergeClients}
        onMerged={async () => {
          showToast("Clients merged successfully.");
          await loadDuplicates();
        }}
      />

      {toastMessage && (
        <div className="fixed bottom-6 right-6 rounded-2xl bg-slate-900 px-4 py-3 text-sm text-white shadow-xl">
          {toastMessage}
        </div>
      )}
    </div>
  );
}
